import React from 'react';

import { Offer, ProductsSequence } from 'common/contracts';
import {
  EPopupSubType,
  EProductPriority,
  ESaleDiscountDisplayType,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';

import { EPopupTriggersAction, PopupsFormFields } from './PopupsForm.consts';

type PopupPayload = Partial<Offer> & {
  sectionId?: string | null;
  displayRule?: { cooldownInMinutes: number; frequency: number };
  coolDownInMinutes?: number;
};

interface IBuildPopupPayloadParams {
  data: PopupsFormFields;
  productsList: Array<{
    productId?: string;
    publisherProductId?: string;
    quantity?: number | string;
    priority?: string;
  }>;
  progressBarFormValues?: {
    progressBarId: string | null;
    progressBarPoints: number | null;
  };
}

const buildProductsSequence = (
  productsList: IBuildPopupPayloadParams['productsList'],
  priceInUsdCents: number
): ProductsSequence[] => {
  const sequence: ProductsSequence[] = productsList.map((product, index) => ({
    index: index + 1,
    priceInUsdCents,
    products: [
      {
        productId: product.productId,
        quantity: product.quantity
      }
    ]
  }));

  if (sequence.length === 0) {
    return [{ index: 1, priceInUsdCents, products: [] }];
  }

  return sequence;
};

const buildTriggeredProductsSequence = (
  productsList: IBuildPopupPayloadParams['productsList'],
  priceInUsdCents: number
): ProductsSequence[] => {
  const products = productsList.map((product) => ({
    productId: product.productId,
    quantity: product.quantity,
    priority: product.priority || EProductPriority.SUB
  }));

  return [{ index: 1, priceInUsdCents, products }];
};

const buildBasePayload = (
  data: PopupsFormFields,
  productsSequence: ProductsSequence[]
): PopupPayload => ({
  active: data.active,
  ...(data.description && { description: data.description }),
  displayName: data.displayName || data.name,
  name: data.name,
  offerUiId: data.offerUiId || data._id,
  priceDiscount: {
    discount: data.discount,
    type: data.priceDiscountType || ESaleDiscountDisplayType.PERCENTAGE
  },
  priority: data.priority ?? undefined,
  productsSequence,
  publisherOfferId: data.publisherOfferId,
  segments: data.segments,
  subType: data.subType,
  tags: data.tags,
  type: data.type as OfferType
});

const buildDailyBonusPayload = ({
  data,
  productsList
}: IBuildPopupPayloadParams): PopupPayload => {
  const productsSequence = buildProductsSequence(productsList, 0);

  return {
    ...buildBasePayload(data, productsSequence),
    coolDownInHours: 1,
    showAfter: 'login',
    startOver: data.startOver,
    triggers: [{ type: 'time', eventName: 'impression', every: 1440 }]
  };
};

const buildPurchaseCompletedPayload = (
  base: PopupPayload,
  data: PopupsFormFields
): PopupPayload => {
  let rules: Array<{ fieldName: string; operator: string; value: string[] }> =
    [];
  const hasOfferExternalIds =
    data.triggersOfferExternalIds && data.triggersOfferExternalIds.length > 0;
  if (hasOfferExternalIds) {
    rules = [
      {
        fieldName: 'publisherOfferId',
        operator: 'in',
        value: data.triggersOfferExternalIds!
      }
    ];
  }

  return {
    ...base,
    showAfter: 'purchase',
    startOver: true,
    triggers: [
      {
        type: 'event',
        eventName: 'purchase',
        every: data.triggersFrequency || 1,
        rules
      }
    ]
  };
};

const buildStoreRefreshPayload = (
  base: PopupPayload,
  data: PopupsFormFields
): PopupPayload => ({
  ...base,
  displayRule: {
    cooldownInMinutes: data.coolDownInMinutes ?? 1,
    frequency: data.triggersFrequency || 1
  },
  schedule: {
    permanent: data.schedule?.permanent ?? true,
    timeFrames: data.schedule?.timeFrames ?? [],
    ...(data.schedule?.interval && { interval: data.schedule.interval })
  }
});

const addPlayerAvailabilityToSequence = (
  sequence: ProductsSequence[],
  playerAvailability: number | undefined
): ProductsSequence[] => {
  if (playerAvailability == null) return sequence;
  return sequence.map((seq) => ({ ...seq, playerAvailability }));
};

const withProgressBarPoints = (
  seq: ProductsSequence,
  progressBarPoints: ReturnType<typeof convertProgressBarPointsToApiFormat>
): ProductsSequence => {
  if (progressBarPoints) {
    return { ...seq, progressBarPoints };
  }
  return seq;
};

const toUsdCents = (price: number | null | undefined): number => {
  if (price === null || price === undefined || Number.isNaN(price)) return 0;
  return new Decimal(price).mul(100).toNumber();
};

const buildTriggeredPopupPayload = ({
  data,
  productsList,
  progressBarFormValues
}: IBuildPopupPayloadParams): PopupPayload => {
  const priceInUsdCents = data.setAsFree ? 0 : toUsdCents(data.price);
  const progressBarPoints = convertProgressBarPointsToApiFormat(
    progressBarFormValues
  );
  const productsSequence = addPlayerAvailabilityToSequence(
    buildTriggeredProductsSequence(productsList, priceInUsdCents).map((seq) =>
      withProgressBarPoints(seq, progressBarPoints)
    ),
    data.playerAvailability
  );
  const base: PopupPayload = {
    ...buildBasePayload(data, productsSequence),
    productSale: {
      sale: data.salePercentage ?? 0,
      type:
        (data.salePercentageDisplayType as ESaleDiscountDisplayType) ||
        ESaleDiscountDisplayType.PERCENTAGE
    }
  };

  if (data.triggersAction === EPopupTriggersAction.STORE_REFRESH) {
    return buildStoreRefreshPayload(base, data);
  }
  return buildPurchaseCompletedPayload(base, data);
};

export const buildPopupPayload = ({
  data,
  productsList,
  progressBarFormValues
}: IBuildPopupPayloadParams): PopupPayload => {
  const isDailyBonus = data.subType === EPopupSubType.DAILY_BONUS;
  if (isDailyBonus) {
    return buildDailyBonusPayload({ data, productsList });
  }
  return buildTriggeredPopupPayload({
    data,
    productsList,
    progressBarFormValues
  });
};

export const extractPlayerAvailability = (
  offer: Partial<Offer>
): number | undefined => {
  const isDailyBonus = offer.subType === EPopupSubType.DAILY_BONUS;
  if (isDailyBonus) return undefined;

  const fromSequence = (
    offer.productsSequence as ProductsSequence[] | undefined
  )?.[0]?.playerAvailability;
  return fromSequence ?? offer.playerAvailability;
};

interface INumberInputHandlers {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const createNumberInputHandlers = (
  setValue: (value: number | string) => void,
  defaultValue: number
): INumberInputHandlers => ({
  onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    if (raw === '') {
      setValue('');
      return;
    }
    setValue(e.target.valueAsNumber);
  },
  onBlur: (e: React.FocusEvent<HTMLInputElement>): void => {
    if (e.target.value === '') {
      setValue(defaultValue);
    }
  }
});
